import { _getLogger } from '@doxyme/analytics';
import ExtensionCapability from '../../capability';
import { type Message, Specs, attachHandler } from '@doxyme/extensions-common';
import type ExtensionInstance from '../../instance';
const { Notification } = Specs;
import NotificationCategory = Specs.Notification.NotificationCategory;

type NotificationSnackbarPayload =
  Specs.Notification.NotificationSnackbarPayload;
type NotificationToastPayload = Specs.Notification.NotificationToastPayload;
type DismissPayload = Specs.Notification.DismissPayload;

type OnNotificationSnackbarHandler = (
  payload: NotificationSnackbarPayload,
  sendMessage: (message: Message) => void,
) => void;

type OnNotificationToastHandler = (
  payload: NotificationToastPayload,
  sendMessage: (message: Message) => void,
) => void;
type OnDismissHandler = (payload: DismissPayload) => void;

type Constraints = Specs.Notification.Constraints;

const logger = _getLogger('notification', {
  component: 'extensions',
  lib: 'bridge',
});

/**
 * Notification Capability
 */
export default class NotificationCapability extends ExtensionCapability<Constraints> {
  static readonly id = Notification.id;

  protected readonly snackbarHandlers: OnNotificationSnackbarHandler[] = [];

  protected readonly toastHandlers: OnNotificationToastHandler[] = [];

  protected readonly dismissHandlers: OnDismissHandler[] = [];

  constructor() {
    super(Notification);

    this.handlers.push(
      [Notification.Actions.Snackbar, this.snackbar.bind(this)],
      [Notification.Actions.Toast, this.toast.bind(this)],
      [Notification.Actions.Dismiss, this.dismiss.bind(this)],
    );
  }

  snackbar(
    payload: NotificationSnackbarPayload,
    instance: ExtensionInstance,
  ): void {
    if (!this.isAllowedNotification(NotificationCategory.Snackbar, instance)) {
      return;
    }
    for (const handler of this.snackbarHandlers) {
      handler(payload, (message: Message) =>
        this.handleMessages(instance, [message]),
      );
    }
  }

  toast(
    payload: NotificationToastPayload,
    instance: ExtensionInstance,
    forceAllow = false,
  ): void {
    if (
      !forceAllow &&
      !this.isAllowedNotification(NotificationCategory.Toast, instance)
    ) {
      return;
    }
    for (const handler of this.toastHandlers) {
      handler(payload, (message: Message) =>
        this.handleMessages(instance, [message]),
      );
    }
  }

  dismiss(payload: DismissPayload): void {
    for (const handler of this.dismissHandlers) {
      handler(payload);
    }
  }

  onSnackbar(handler: OnNotificationSnackbarHandler): () => void {
    return attachHandler<OnNotificationSnackbarHandler>(
      this.snackbarHandlers,
      handler,
    );
  }

  onToast(handler: OnNotificationToastHandler): () => void {
    return attachHandler<OnNotificationToastHandler>(
      this.toastHandlers,
      handler,
    );
  }

  onDismiss(handler: OnDismissHandler): () => void {
    return attachHandler<OnDismissHandler>(this.dismissHandlers, handler);
  }

  private isAllowedNotification(
    category: NotificationCategory,
    instance: ExtensionInstance,
  ): boolean {
    let isValid = false;
    for (const config of this.configsBy(instance)) {
      isValid = config.categories.includes(category);
      if (isValid) {
        break;
      }
    }
    if (!isValid) {
      logger.warn(
        `Rejecting action for unregisterd notification category: ${category}`,
      );
    }
    return isValid;
  }
}
